import React, { useEffect, useState, Fragment, useCallback, useRef } from 'react';
import {
  Navbar,
  Nav,
  NavbarToggler,
  Collapse,
  NavItem,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  NavLink,
  Container
} from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import Search from './SearchComponent';

const Header = props => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isPwChangeModalOpen, setIsPwChangeModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navbarRef = useRef(null);
  const searchCloseRef = useRef(null); // Ref to trigger search dropdown close

  // Initialize from localStorage or default
  const [showAll, setShowAll] = useState(() => {
    const saved = localStorage.getItem('showAll');
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Get current location to determine which page we're on
  const location = useLocation();
  const isProPage = location.pathname === '/proInputs';
  const isChronPage = location.pathname === '/chronicle';
  const isDataPage = location.pathname === '/' || location.pathname === '/inputs';

  // Close navbar when route changes
  useEffect(() => {
    setIsNavOpen(false);
  }, [location.pathname]);

  // Use the appropriate view based on current page
  const view = isProPage ? props.proView : props.dataView;
  const setView = isProPage ? props.setProView : props.setDataView;
  const getView = isProPage ? props.getProView : props.getDataView;

  // Use the appropriate keyword based on current page
  const keyword = isProPage ? props.proKeyword : isChronPage ? props.chronKeyword : props.dataKeyword;
  const getKeyword = isProPage ? props.getProKeyword : isChronPage ? props.getChronKeyword : props.getDataKeyword;

  // Persist to localStorage when changed
  useEffect(() => {
    localStorage.setItem('showAll', JSON.stringify(showAll));
  }, [showAll]);

  useEffect(() => {
    document.body.classList.add('fixed-header');
  }, []);

  // Close hamburger menu when clicking outside the navbar
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close navbar if clicking outside
      if (isNavOpen && navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsNavOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isNavOpen]);

  const toggleDropdownOpen = () => setDropdownOpen(!dropdownOpen);
  const toggleNav = () => setIsNavOpen(!isNavOpen);
  const closeNavbar = () => setIsNavOpen(false);
  const toggleModal = () => setIsModalOpen(!isModalOpen);
  const togglePwChangeModal = () =>
    setIsPwChangeModalOpen(!isPwChangeModalOpen);
  const toggleSignupModal = () => setIsSignupModalOpen(!isSignupModalOpen);

  const handleLogin = event => {
    event.preventDefault();
    toggleModal();
    props.loginUser({
      username: usernameRef.current.value,
      password: passwordRef.current.value
    });
    if (isNavOpen) {
      toggleNav();
    }
  };

  const handlePwChange = event => {
    event.preventDefault();
    togglePwChangeModal();
    props.pwChangeUser({
      username: pwChangeUsernameRef.current.value,
      oldpassword: oldpasswordRef.current.value,
      newpassword: newpasswordRef.current.value
    });
    if (isNavOpen) {
      toggleNav();
    }
  };

  const handleSignup = event => {
    event.preventDefault();
    if (!signupEmailRef.current.value) {
      alert('No EMAIL');
    } else {
      toggleSignupModal();
      props.signupUser({
        username: signupUsernameRef.current.value,
        password: signupPasswordRef.current.value,
        email: signupEmailRef.current.value
      });
      if (isNavOpen) {
        toggleNav();
      }
    }
  };

  const handleLogout = () => {
    props.logoutUser();
    if (isNavOpen) {
      toggleNav();
    }
  };

  const handleFiles = files => {
    var reader = new FileReader();
    reader.onload = function (e) {
      alert(reader.result);
    };
    reader.readAsText(files[0]);
  };

  // ✅ Memoize findKirks to prevent it from changing on every render
  const findKirks = useCallback(
    newKeyword => {
      getKeyword(newKeyword);
    },
    [getKeyword]
  );

  const toggleShowAll = () => {
    props.setShowAll(prev => {
      const newVal = !prev;
      props.getShowAll && props.getShowAll(newVal);
      if (isNavOpen) toggleNav();
      return newVal;
    });
  };

  const toggleView = () => {
    const newVal = !view;
    setView(newVal);
    getView && getView(newVal);
    if (isNavOpen) toggleNav();
  };

  const usernameRef = React.createRef();
  const passwordRef = React.createRef();
  const pwChangeUsernameRef = React.createRef();
  const oldpasswordRef = React.createRef();
  const newpasswordRef = React.createRef();
  const signupUsernameRef = React.createRef();
  const signupPasswordRef = React.createRef();
  const signupEmailRef = React.createRef();

  const foundKirks = props.kirks.filter(curr =>
    curr.subject.includes(keyword || '')
  );

  return (
    <Fragment>
      <Navbar
        dark
        expand='xl'
        className='header'
        fixed='top'
      >
        <Container>
          <div className='container'>
            <div className='row'>
              <div className={isNavOpen ? 'col-6' : 'col-6 align-self-center'}>
                <Search findKirks={findKirks} initialKeyword={keyword} />
              </div>
              <div className='col-6' align='right'>
                <div className='ml-auto' ref={navbarRef}>
                  <NavbarToggler onClick={toggleNav} />
                  <Collapse isOpen={isNavOpen} navbar className='ml-auto'>
                    <Nav className='ml-auto'>
                      <Button
                        outline
                        color={props.showAll ? 'primary' : 'secondary'}
                        onClick={toggleShowAll}
                        className={isNavOpen ? 'header-buttons' : ''}
                      >
                        {props.showAll ? 'Mine' : 'All'}
                      </Button>
                    </Nav>
                    <Nav className='ml-auto'>
                      <Button
                        outline
                        color={view ? 'primary' : 'secondary'}
                        onClick={toggleView}
                      >
                        {view ? 'List' : 'S/S'}
                      </Button>
                    </Nav>
                    <Nav className='ml-auto'>
                      <Link to='/kirks' className='nav-link' onClick={closeNavbar}>
                        Kirk
                      </Link>
                    </Nav>
                    <Nav className='ml-auto'>
                      <Link to='/' className='nav-link' onClick={closeNavbar}>
                        Data
                      </Link>
                    </Nav>
                    <Nav className='ml-auto'>
                      <Link to='/proInputs' className='nav-link' onClick={closeNavbar}>
                        Pro
                      </Link>
                    </Nav>
                    <Nav className='ml-auto'>
                      <Link to='/chronicle' className='nav-link' onClick={closeNavbar}>
                        Chron
                      </Link>
                    </Nav>
                    <Nav className='ml-auto'>
                      <NavItem style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '5px' }}>
                        {!props.auth.isAuthenticated ? (
                          <>
                            <Button outline onClick={() => { toggleSignupModal(); closeNavbar(); }}>
                              Signup
                              {props.auth.isFetching ? (
                                <span className='fa fa-spinner fa-pulse fa-fw'></span>
                              ) : null}
                            </Button>
                            <Button outline onClick={() => { toggleModal(); closeNavbar(); }}>
                              Login
                              {props.auth.isFetching ? (
                                <span className='fa fa-spinner fa-pulse fa-fw'></span>
                              ) : null}
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className='navbar-text mr-2'>{props.auth.user.username}</span>
                            <Button outline onClick={handleLogout}>
                              Logout
                              {props.auth.isFetching ? (
                                <span className='fa fa-spinner fa-pulse fa-fw'></span>
                              ) : null}
                            </Button>
                            <Button outline onClick={() => { togglePwChangeModal(); closeNavbar(); }}>
                              <span className='fa fa-key'></span>
                              {props.auth.isFetching ? (
                                <span className='fa fa-spinner fa-pulse fa-fw'></span>
                              ) : null}
                            </Button>
                          </>
                        )}
                      </NavItem>
                    </Nav>
                  </Collapse>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Navbar>
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Login</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleLogin}>
            <FormGroup>
              <Label htmlFor='username'>Username</Label>
              <Input
                type='text'
                id='username'
                name='username'
                innerRef={usernameRef}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor='password'>Password</Label>
              <Input
                type='password'
                id='password'
                name='password'
                innerRef={passwordRef}
              />
            </FormGroup>
            <p className='forgot-password text-right'>
              <Link to={'/users/forgot-password'} onClick={toggleModal}>
                Forgot password?
              </Link>
            </p>
            <Button type='submit' value='submit' color='primary'>
              Login
            </Button>
          </Form>
        </ModalBody>
      </Modal>

      <Modal isOpen={isPwChangeModalOpen} toggle={togglePwChangeModal}>
        <ModalHeader toggle={togglePwChangeModal}>Password Change</ModalHeader>
        <ModalBody>
          <Form onSubmit={handlePwChange}>
            <FormGroup>
              <Label htmlFor='pwChangeUsername'>Username</Label>
              <Input
                type='text'
                id='pwChangeUsername'
                name='pwChangeUsername'
                innerRef={pwChangeUsernameRef}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor='oldpassword'>Old Password</Label>
              <Input
                type='password'
                id='oldpassword'
                name='oldpassword'
                innerRef={oldpasswordRef}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor='newpassword'>New Password</Label>
              <Input
                type='password'
                id='newpassword'
                name='newpassword'
                innerRef={newpasswordRef}
              />
            </FormGroup>
            <Button type='submit' value='submit' color='primary'>
              Change Password
            </Button>
          </Form>
        </ModalBody>
      </Modal>

      <Modal isOpen={isSignupModalOpen} toggle={toggleSignupModal}>
        <ModalHeader toggle={toggleSignupModal}>Signup</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSignup}>
            <FormGroup>
              <Label htmlFor='signupUsername'>Username</Label>
              <Input
                type='text'
                id='signupUsername'
                name='signupUsername'
                innerRef={signupUsernameRef}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor='signupPassword'>Password</Label>
              <Input
                type='password'
                id='signupPassword'
                name='signupPassword'
                innerRef={signupPasswordRef}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor='signupEmail'>Email</Label>
              <Input
                type='email'
                id='signupEmail'
                name='signupEmail'
                innerRef={signupEmailRef}
              />
            </FormGroup>
            <Button type='submit' value='submit' color='primary'>
              Signup
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default Header;
